import {HttpErrorResponse} from '@angular/common/http';
import {type Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {firstValueFrom} from 'rxjs';
import {SmConfirmationDialogService} from '../shared/components/sm-confirmation-dialog/sm-confirmation-dialog.service';
import {wasSystemApiErrorPresented} from '../shared/errors/local-api-error';
import type {SmContextMenuItemModel} from '../shared/components/sm-context-menu-item/sm-context-menu-item.component';
import type {AppLanguageCode} from '../shared/i18n/app-language';
import {buildPlatformAwareRoutePathname} from '../shared/i18n/app-language';
import {ContainersService, type ContainerDeleteReason} from './containers.service';

export type ContainerActionPathSegment = {
  id: string;
  name: string;
};

export type ContainerActionTarget = {
  id: string;
  name: string;
  description: string | null;
  imageUrl: string | null;
  canEdit?: boolean;
  isSharedRoot?: boolean;
};

export type ContainerActionMenuContext = {
  containerPath: ContainerActionPathSegment[];
  languageCode: AppLanguageCode;
  returnUrl: string;
  closeMenu?: () => void;
  afterDelete?: () => void | Promise<void>;
};

type ContainerActionMenuDependencies = {
  confirmationDialogService: SmConfirmationDialogService;
  containersService: ContainersService;
  translateService: TranslateService;
};

const CONTAINER_DELETE_DIALOG_KEYS = {
  title: 'containers.page.deleteDialogTitle',
  cancelLabel: 'common.actions.cancel',
  closeLabel: 'containers.page.deleteDialogCloseLabel',
  confirmLabel: 'containers.page.deleteDialogDeleteLabel',
  questionPrefix: 'containers.page.deleteDialogQuestionPrefix',
  questionSuffix: 'containers.page.deleteDialogQuestionSuffix',
  warning: 'containers.page.deleteDialogWarning',
  error: 'containers.page.deleteError',
  errorNotFound: 'containers.page.deleteErrors.notFound',
  errorHasChildren: 'containers.page.deleteErrors.hasChildren',
  errorHasItems: 'containers.page.deleteErrors.hasItems',
  errorFallback: 'containers.page.deleteErrors.fallback',
  shareRouteSuffix: '/share',
  editRouteSuffix: '/edit',
  createRouteSuffix: '/create',
} as const;

export async function handleContainerMenuItemSelect(
  router: Router,
  menuItem: SmContextMenuItemModel,
  context: ContainerActionMenuContext,
  container: ContainerActionTarget,
  dependencies: ContainerActionMenuDependencies,
): Promise<void> {
  context.closeMenu?.();

  if (container.canEdit === false) {
    return;
  }

  if (menuItem.id === 'numbered-subcontainers') {
    await router.navigateByUrl(buildPlatformAwareRoutePathname(`/inventory/containers/${container.id}`, context.languageCode), {
      state: {containerPath: context.containerPath, inventoryAction: 'numbered-subcontainers'},
    });
    return;
  }

  if (menuItem.id === 'delete') {
    await prepareContainerDelete(router, context, container, dependencies);
    return;
  }

  if (menuItem.id === 'share') {
    const target = buildPlatformAwareRoutePathname(
      `/inventory/containers/${container.id}${CONTAINER_DELETE_DIALOG_KEYS.shareRouteSuffix}`,
      context.languageCode,
    );

    void router.navigateByUrl(target, {
      state: {
        containerPath: context.containerPath,
        returnUrl: context.returnUrl,
      },
    });
    return;
  }

  if (menuItem.id === 'move') {
    const target = buildPlatformAwareRoutePathname('/relocation', context.languageCode);
    void router.navigateByUrl(target, {
      state: {
        source: {
          kind: 'container',
          container: {
            id: container.id,
            name: container.name,
            description: container.description,
            imageUrl: container.imageUrl,
            containerPath: context.containerPath,
          },
        },
        returnUrl: context.returnUrl,
      },
    });
    return;
  }

  if (menuItem.id === 'move-into') {
    const target = buildPlatformAwareRoutePathname('/relocation', context.languageCode);
    const returnUrl = buildPlatformAwareRoutePathname(`/inventory/containers/${container.id}`, context.languageCode);
    void router.navigateByUrl(target, {
      state: {
        destination: {
          id: container.id,
          name: container.name,
          description: container.description,
          imageUrl: container.imageUrl,
          hasAssignedQrCode: false,
          path: context.containerPath,
        },
        returnUrl,
      },
    });
    return;
  }

  if (menuItem.id === 'new') {
    const target = buildPlatformAwareRoutePathname(
      `/inventory/containers/${container.id}${CONTAINER_DELETE_DIALOG_KEYS.createRouteSuffix}`,
      context.languageCode,
    );

    void router.navigateByUrl(target, {
      state: {
        containerPath: context.containerPath,
      },
    });
    return;
  }

  if (menuItem.id === 'edit') {
    const target = buildPlatformAwareRoutePathname(
      `/inventory/containers/${container.id}${CONTAINER_DELETE_DIALOG_KEYS.editRouteSuffix}`,
      context.languageCode,
    );

    void router.navigateByUrl(target, {
      state: {
        containerPath: context.containerPath,
        returnUrl: context.returnUrl,
      },
    });
  }
}

const DELETE_REASON_KEYS: Record<ContainerDeleteReason, string> = {
  HasItems: 'containers.page.deleteErrors.hasItems',
  HasSubLocations: 'containers.page.deleteErrors.hasChildren',
  HasShares: 'containers.page.deleteErrors.hasShares',
};

function deleteConflictReason(error: unknown): ContainerDeleteReason | null {
  if (!(error instanceof HttpErrorResponse) || error.status !== 409) return null;
  const code = error.error?.error?.code;
  switch (code) {
    case 'CanNotDeleteItemContainerWithItems': return 'HasItems';
    case 'CanNotDeleteItemContainerWithChildren': return 'HasSubLocations';
    case 'CanNotDeleteItemContainerWithShares': return 'HasShares';
    default: return null;
  }
}

function buildContainerDeleteErrorMessage(translateService: TranslateService, error: unknown): string {
  const reason = deleteConflictReason(error);
  if (reason) return translateService.instant(DELETE_REASON_KEYS[reason]);
  const key = error instanceof HttpErrorResponse && error.status === 404
    ? CONTAINER_DELETE_DIALOG_KEYS.errorNotFound : CONTAINER_DELETE_DIALOG_KEYS.errorFallback;
  return translateService.instant(key);
}

async function showDeleteBlock(
  router: Router,
  context: ContainerActionMenuContext,
  container: ContainerActionTarget,
  dependencies: ContainerActionMenuDependencies,
  reasons: ContainerDeleteReason[],
  error?: unknown,
): Promise<void> {
  const t = (key: string) => dependencies.translateService.instant(key);
  const showItems = reasons.includes('HasItems');
  const showAll = await dependencies.confirmationDialogService.open({
    title: t(CONTAINER_DELETE_DIALOG_KEYS.title),
    description: reasons.length
      ? reasons.map(reason => t(DELETE_REASON_KEYS[reason] ?? CONTAINER_DELETE_DIALOG_KEYS.errorFallback)).join(' ')
      : buildContainerDeleteErrorMessage(dependencies.translateService, error),
    dialogType: 'warning',
    cancelLabel: t('common.actions.cancel'),
    closeLabel: t(CONTAINER_DELETE_DIALOG_KEYS.closeLabel),
    confirmLabel: t('containers.page.quantityFilter.showAll'),
    confirmTone: 'primary',
    showConfirmButton: showItems,
  });
  if (showAll && showItems) {
    await router.navigateByUrl(
      buildPlatformAwareRoutePathname(`/inventory/containers/${container.id}`, context.languageCode) + '?visibility=all',
      {state: {containerPath: context.containerPath}},
    );
  }
}

async function prepareContainerDelete(
  router: Router,
  context: ContainerActionMenuContext,
  container: ContainerActionTarget,
  dependencies: ContainerActionMenuDependencies,
): Promise<void> {
  try {
    const eligibility = await firstValueFrom(dependencies.containersService.getDeleteEligibility(container.id));
    if (!eligibility.canDelete) {
      await showDeleteBlock(router, context, container, dependencies, eligibility.reasons);
      return;
    }
  } catch (error) {
    if (wasSystemApiErrorPresented(error)) return;
    await showDeleteBlock(router, context, container, dependencies, [], error);
    return;
  }
  await openDeleteConfirmation(router, context, container, dependencies);
}

async function openDeleteConfirmation(
  router: Router,
  context: ContainerActionMenuContext,
  container: ContainerActionTarget,
  dependencies: ContainerActionMenuDependencies,
): Promise<void> {
  let conflict: ContainerDeleteReason | null = null;
  await dependencies.confirmationDialogService.open({
    title: dependencies.translateService.instant(CONTAINER_DELETE_DIALOG_KEYS.title),
    cancelLabel: dependencies.translateService.instant(CONTAINER_DELETE_DIALOG_KEYS.cancelLabel),
    confirmLabel: dependencies.translateService.instant(CONTAINER_DELETE_DIALOG_KEYS.confirmLabel),
    closeLabel: dependencies.translateService.instant(CONTAINER_DELETE_DIALOG_KEYS.closeLabel),
    confirmTone: 'danger',
    contentAlign: 'center',
    compactActions: true,
    heroIconName: 'trash-outline',
    showCloseButton: false,
    bodyBlocks: [
      {
        segments: [
          {text: dependencies.translateService.instant(CONTAINER_DELETE_DIALOG_KEYS.questionPrefix)},
          {text: ` ${container.name}`, emphasis: true},
          {text: dependencies.translateService.instant(CONTAINER_DELETE_DIALOG_KEYS.questionSuffix)},
        ],
      },
      {
        segments: [
          {text: dependencies.translateService.instant(CONTAINER_DELETE_DIALOG_KEYS.warning)},
        ],
        tone: 'danger',
      },
    ],
    onConfirm: async () => {
      try {
        await firstValueFrom(dependencies.containersService.deleteContainerById(container.id));
      } catch (error) {
        conflict = deleteConflictReason(error);
        if (!conflict) throw error;
        return;
      }
      await context.afterDelete?.();
    },
    errorFormatter: (error) => buildContainerDeleteErrorMessage(dependencies.translateService, error),
    errorFallback: dependencies.translateService.instant(CONTAINER_DELETE_DIALOG_KEYS.error),
  });
  if (conflict) await showDeleteBlock(router, context, container, dependencies, [conflict]);
}
